import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trophy, Sparkles, Calendar, Crown, PartyPopper, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { softConfettiDrizzle } from './party';
import './rr-party.css';

/**
 * Employee-facing R&R page.
 * Before the admin flips `winners_published`, this screen shows a hero
 * "Winners coming soon" banner with a live countdown to `reveal_at`.
 * Once published, it renders a gallery of winning cards grouped by award.
 *
 * Nominations are strictly admin-only from Feb 2026 onwards — this component
 * NEVER opens the nomination form. That flow lives in `AdminRRConsole` only.
 */
export default function RRWinnersPage({ api }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [celebrated, setCelebrated] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/rr/winners/public');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Rewards & Recognition');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    // Live 1-Hz clock for countdown
    const t = setInterval(() => setTick(k => k + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // One-time confetti drizzle when winners load in
  useEffect(() => {
    if (data?.published && !celebrated) {
      setCelebrated(true);
      softConfettiDrizzle();
    }
  }, [data?.published, celebrated]);

  const countdown = useMemo(() => {
    if (!data?.reveal_at) return null;
    const target = new Date(data.reveal_at).getTime();
    const now = Date.now();
    const diff = target - now;
    if (diff <= 0) return { done: true, d: 0, h: 0, m: 0, s: 0 };
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { done: false, d, h, m, s };
  }, [data?.reveal_at, tick]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]" data-testid="rr-winners-loading">
        <Loader2 className="w-6 h-6 animate-spin text-fuchsia-600" />
      </div>
    );
  }

  const published = data?.published;
  const winners = data?.winners || [];

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto rr-rainbow-cursor" data-testid="rr-winners-page">
      {/* Hero */}
      <div className="relative overflow-hidden mb-8 rounded-3xl border border-white/40 shadow-xl bg-gradient-to-br from-fuchsia-500 via-purple-600 to-indigo-700 text-white p-8 md:p-12">
        <div className="absolute inset-0 opacity-40 mix-blend-overlay rr-hero-noise" aria-hidden="true" />
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-yellow-300/30 blur-3xl animate-pulse" aria-hidden="true" />
        <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-pink-400/30 blur-3xl" aria-hidden="true" />

        <div className="relative flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-4">
          <Sparkles className="w-3.5 h-3.5" />
          <span>{data?.edition?.name || 'HED Rewards & Recognition 2026'}</span>
        </div>

        {!published && (
          <div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-3" data-testid="rr-winners-heading">
              Winners coming soon ✨
            </h1>
            <p className="text-white/85 max-w-2xl text-sm md:text-base mb-8">
              Nominations are in — every one of you was seen, heard, and considered. Winners will be revealed on{' '}
              <span className="font-bold">5 September 2026 · 11:30 PM IST</span>. Until then, take a bow — the whole
              office is quietly writing about someone incredible right now, and it might just be you.
            </p>

            {countdown && (
              <div className="grid grid-cols-4 gap-3 md:gap-6 max-w-2xl" data-testid="rr-winners-countdown">
                {['d','h','m','s'].map(k => (
                  <div key={k} className="bg-white/15 backdrop-blur-sm border border-white/20 rounded-2xl p-4 md:p-6 text-center">
                    <div className="text-3xl md:text-5xl font-black tabular-nums leading-none" data-testid={`rr-countdown-${k}`}>
                      {String(countdown[k]).padStart(2, '0')}
                    </div>
                    <div className="text-[10px] md:text-xs uppercase tracking-widest mt-2 text-white/70">
                      {k === 'd' ? 'Days' : k === 'h' ? 'Hours' : k === 'm' ? 'Minutes' : 'Seconds'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {countdown?.done && (
              <p className="text-yellow-200 text-sm mt-6 font-semibold">
                🎉 The moment is here — winners will show up right on this page any minute.
              </p>
            )}

            <div className="mt-8 flex items-center gap-2 text-xs text-white/70">
              <Calendar className="w-3.5 h-3.5" />
              <span>Judged by Management and HR</span>
            </div>
          </div>
        )}

        {published && (
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-300 text-purple-900 text-xs font-black uppercase tracking-widest mb-3 animate-pulse">
              <Crown className="w-3.5 h-3.5" /> Winners Revealed
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-3" data-testid="rr-winners-heading">
              And the winners are…
            </h1>
            <p className="text-white/85 max-w-2xl text-sm md:text-base">
              A huge congratulations to everyone recognised this year — and a heartfelt thank-you to every nominator
              who made the case. Screenshot, share, and celebrate the humans below.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 border border-white/20">
                <Trophy className="w-3.5 h-3.5" />
                {data.total_winners} winner{data.total_winners === 1 ? '' : 's'}
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 border border-white/20">
                <PartyPopper className="w-3.5 h-3.5" />
                {winners.length} categories
              </span>
            </div>
          </div>
        )}
      </div>

      {published && winners.length === 0 && (
        <div className="text-center py-16 text-neutral-500" data-testid="rr-winners-empty">
          Winners are being finalised — check back shortly.
        </div>
      )}

      {published && winners.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="rr-winners-grid">
          {winners.map((block) => (
            <Card key={block.award_id} className="p-5 border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 via-white to-amber-50 hover:shadow-xl transition-shadow" data-testid={`rr-winner-card-${block.award_id}`}>
              <div className="flex items-start gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-600 grid place-items-center shadow-inner shadow-yellow-800/30">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-amber-800/70 font-semibold">
                    {block.award_department || 'Agency Wide'}
                  </p>
                  <h3 className="text-base font-bold text-neutral-900 leading-tight">{block.award_name}</h3>
                  {block.description && (
                    <p className="text-xs text-neutral-500 italic mt-1 line-clamp-2">{block.description}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                {block.winners.map((w, i) => {
                  const initials = (w.name || '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase();
                  const hues = ['from-pink-400 to-fuchsia-500','from-amber-400 to-orange-500','from-emerald-400 to-teal-500','from-sky-400 to-indigo-500','from-purple-400 to-pink-500'];
                  const gradient = hues[(w.name?.length || 0) % hues.length];
                  return (
                  <div key={i} className="rounded-xl bg-white/70 border border-amber-100 p-3">
                    <div className="flex items-center gap-3 mb-1">
                      {w.photo_url ? (
                        <img src={w.photo_url} alt={w.name} className="w-10 h-10 rounded-full object-cover border-2 border-amber-300 shadow-sm flex-shrink-0" />
                      ) : (
                        <div className={`w-10 h-10 rounded-full grid place-items-center bg-gradient-to-br ${gradient} text-white font-bold text-sm border-2 border-white shadow-sm flex-shrink-0`}>
                          {initials}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Crown className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          <p className="text-base font-bold text-neutral-900 truncate">{w.name}</p>
                        </div>
                        <p className="text-xs text-neutral-600 truncate">
                          {[w.designation, w.department].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </div>
                    </div>
                    {w.judge_note && (
                      <p className="text-xs text-neutral-700 mt-2 pl-2 border-l-2 border-amber-300 italic">
                        “{w.judge_note}”
                      </p>
                    )}
                    {!w.judge_note && w.note && (
                      <p className="text-xs text-neutral-500 mt-2 pl-2 border-l-2 border-neutral-200 italic line-clamp-3">
                        {w.note}
                      </p>
                    )}
                    {w.nominator_name && (
                      <p className="text-[10px] text-neutral-400 mt-2">— nominated by {w.nominator_name}</p>
                    )}
                  </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
